import {ChangeDetectionStrategy, ChangeDetectorRef, Component, OnDestroy} from '@angular/core';
import {ICellRendererAngularComp} from "ag-grid-angular";
import {CellClickedEvent, IAfterGuiAttachedParams, ICellRendererParams} from 'ag-grid-community';
import {MessageBusService} from "../../message-bus.service";
import {LastQuoteCacheService} from "../../last-quote-cache.service";
import {
    OpgCellSelectedMessage,
    OpgExpirationChangeMessage,
    OptionsPricingGridComponentBase
} from "../options-pricing-grid-base.component";
import {PricingGridRow} from "../model/pricing-grid.row";
import {DetectMethodChanges, isValidNumber, isVoid, makeDayOfWeekDate} from "../../utils";
import {makeOptionTicker, OptionType, parseOptionTicker} from "../../options-common/options.model";
import {QuoteDto} from "../../shell-communication/dtos/quote-dto.class";
import {defaultPriceCellFormatterWithDollarSign} from "../../ag-grid-contrib";
import {filter} from "rxjs/operators";
import {PricingGridStrategyColumn} from "../model/pricing-grid-strategy.column";
import {PricingGridLeg} from "../model/pricing-grid.leg";
import {MarketSide} from "../../trading-model/market-side.enum";
import {Subscription} from "rxjs";
import {HedgePosition} from "../../hedging-grid/data-model/hedge-position";
import {matches} from "linq";
import {PanelChangeDetectionService} from '../../panel-change-detection.service';

interface IPriceCellRendererParams {
    component: OptionsPricingGridComponentBase;
    optionType: 'Call' | 'Put';
    pmdColumn: 'own' | 'trans'
}

function getColor(leg: PricingGridLeg) {
    return leg.side === MarketSide.Buy ? '#00007c' : 'darkred';
}

@Component({
    selector: 'opg-price-column-cell-renderer',
    templateUrl: 'opg-price-column-cell-renderer.component.html',
    styleUrls: ['opg-price-column-cell-renderer.component.scss'],
    changeDetection: ChangeDetectionStrategy.OnPush
})

export class OpgPriceColumnCellRendererComponent implements ICellRendererAngularComp, OnDestroy {

    constructor(
        private readonly _changeDetector: ChangeDetectorRef,
        private readonly _messageBus: MessageBusService,
        private readonly _lastQuoteCache: LastQuoteCacheService,
        private readonly _panelCd: PanelChangeDetectionService
    ) {
    }

    private _params: (ICellRendererParams & IPriceCellRendererParams);

    private _currentTicker: string;
    private _calendarizedTicker: string;
    private _legColor: string;
    private readonly _subscriptions: Subscription[] = [];

    get ticker(): string {
        return this.calendarized ? this._calendarizedTicker : this._currentTicker;
    }

    price: string;

    qty: number;

    get backgroundColor(): string | undefined {
        if (this.isInRange && isVoid(this._legColor)) {
            return 'lemonchiffon';
        }
        return this._legColor;
    }

    get foregroundColor(): string | undefined {
        if (this.isInRange && isVoid(this._legColor)) {
            return 'black';
        }
        return undefined;
    }

    get isPmdColumn(): boolean {
        return !isVoid(this._params.pmdColumn);
    }


    calendarized = false;

    dow: string;

    isInRange: boolean;

    @DetectMethodChanges()
    refresh(params: any): boolean {
        this.onQuote();
        return true;
    }

    @DetectMethodChanges()
    agInit(params: (ICellRendererParams & IPriceCellRendererParams)): void {
        this._params = params;

        // ag-Grid recycles cell renderer instances; unsubscribe any prior subs from
        // a previous agInit before pushing new ones. Otherwise subs accumulate every
        // time the cell is reused (column move, column visibility toggle, data reset).
        this._subscriptions.forEach(s => s.unsubscribe());
        this._subscriptions.length = 0;

        // Calendarized ticker from prior init, if any, also needs releasing.
        if (!isVoid(this._calendarizedTicker)) {
            this._lastQuoteCache.unsubscribeTicker(this._calendarizedTicker);
            this._calendarizedTicker = undefined;
        }

        this._subscriptions.push(
            this._messageBus.of<QuoteDto[]>('QuoteDto')
                .pipe(
                    filter(msg => {
                        const arr = msg.payload;
                        if (!arr || arr.length === 0) {
                            return false;
                        }
                        const cur = this._currentTicker;
                        const cal = this._calendarizedTicker;
                        if (!cur && !cal) {
                            return false;
                        }
                        for (let i = 0; i < arr.length; i++) {
                            const t = arr[i]?.ticker;
                            if (!t) { continue; }
                            if (t === cur || t === cal) {
                                return true;
                            }
                        }
                        return false;
                    })
                )
                .subscribe(msg => this.onQuote())
        );

        this._subscriptions.push(
            this._messageBus.of<OpgExpirationChangeMessage>('Opg.ExpirationChanged')
                .pipe(
                    filter(msg =>
                        msg.payload.component === this._params.component
                    )
                )
                .subscribe(msg => this.onExpirationChange(msg.payload))
        );

        this._subscriptions.push(
            this._messageBus.of<OpgCellSelectedMessage>('Opg.CellSelected')
                .pipe(
                    filter(msg =>
                        msg.payload.component === this._params.component
                    )
                )
                .subscribe(msg => this.onCellSelected())
        );

        this._subscriptions.push(
            this._messageBus.of<any>('Opg.TargetRangeChanged')
                .pipe(
                    filter(msg =>
                        msg.payload.component === this._params.component
                    )
                )
                .subscribe(msg => this.onRangeHighlightChanged())
        );

        this._subscriptions.push(
            this._messageBus.of<any>('Opg.StrategyApplied')
                .pipe(
                    filter(msg =>
                        msg.payload.component === this._params.component
                    )
                ).subscribe(msg => this.onStrategyApplied())
        );

        this._subscriptions.push(
            this._messageBus.of<any>('Pmd.HedgeSelected')
                .pipe(
                    filter(msg =>
                        msg.payload.component === this._params.component
                    )
                ).subscribe(msg => {
                // this.highlightOwnLegs();
                this.onCellSelected();
            })
        )

        this._currentTicker = this.makeCellTicker(params);
        // console.log('[-/+] 0.0 agInit() ', this._currentTicker);
        // this._lastQuoteCache.subscribeTicker(this._currentTicker);

        this.onQuote();
        this.onRangeHighlightChanged();
        this.onCellSelected();
    }

    @DetectMethodChanges()
    private onStrategyApplied() {
        this._legColor = undefined;
        this.qty = undefined;
        this.onCellSelected();
        this._changeDetector.detectChanges();
    }

    afterGuiAttached?(params?: IAfterGuiAttachedParams): void {
        //
    }

    private makeCellTicker(params: ICellRendererParams & IPriceCellRendererParams): string {
        const rowData = this._params.data as PricingGridRow;

        let exp = this._params.component.selectedExpiration;

        const strike = rowData.rowStrike;

        const optionType = this._params.optionType;

        const ticker = makeOptionTicker(exp, optionType, strike);

        return ticker;
    }

    @DetectMethodChanges()
    private onQuote() {

        if (isVoid(this.ticker)) {
            this.price = undefined;
            return;
        }

        const mid = this._lastQuoteCache.getLastQuote(this.ticker)?.mid;

        if (!isValidNumber(mid, true)) {
            this.price = null;
            return;
        }

        this.price = defaultPriceCellFormatterWithDollarSign({value: mid} as any);
    }

    @DetectMethodChanges()
    private onExpirationChange(payload: OpgExpirationChangeMessage) {
        if (!isVoid(this._currentTicker)) {
            // console.log('[-/+] 0.1 onExpirationChange() - unsub ', this._currentTicker);
            // this._lastQuoteCache.unsubscribeTicker(this._currentTicker);
        }

        if (!isVoid(this._calendarizedTicker)) {
            // this._lastQuoteCache.unsubscribeTicker(this._calendarizedTicker);
        }


        this.price = undefined;

        this._currentTicker = this.makeCellTicker(this._params);

        // console.log('[-/+] 0.2 onExpirationChange() - sub ', this._currentTicker);

        // this._lastQuoteCache.subscribeTicker(this._currentTicker);
    }

    @DetectMethodChanges()
    private onCellSelected(): void {

        this._legColor = undefined;
        this.qty = undefined;
        this.calendarized = false;

        if (!isVoid(this._calendarizedTicker)) {
            this._lastQuoteCache.unsubscribeTicker(this._calendarizedTicker);
            this._calendarizedTicker = undefined;
        }

        this.dow = undefined;

        if (this._params.pmdColumn === 'own') {
            this.highlightOwnLegs();
            return;
        }

        if (this._params.component.selectedCallCell) {
            this.onCellSelectedInternal(this._params.component.selectedCallCell);
        }

        if (this._params.component.selectedPutCell) {
            this.onCellSelectedInternal(this._params.component.selectedPutCell);
        }
    }

    private onCellSelectedInternal(theCell: CellClickedEvent) {

        if (isVoid(theCell)) {
            return;
        }

        const callOrPutCell = theCell.colDef.field.indexOf('-call') >= 0
            ? 'Call'
            : 'Put';

        if (callOrPutCell !== this._params.optionType) {
            return;
        }

        const cellRow = theCell.data as PricingGridRow;

        const objAttribute = theCell.colDef.field.split('.')[0];

        const column = cellRow[objAttribute] as PricingGridStrategyColumn;

        if (!column) {
            return;
        }

        if (!this.isPmdColumn) {
            this.highlightOutcomeLegs(column);
        } else {
            if (this._params.pmdColumn === 'trans') {
                this.highlightTransLegs(column);
            } else if (this._params.pmdColumn === 'own') {
                this.highlightOwnLegs();
            }
        }
    }

    private highlightOutcomeLegs(column: PricingGridStrategyColumn) {
        const matchingPriceOutcomeLeg = column.legs
            .find(x => x.strike === this._params.data.rowStrike);

        if (isVoid(matchingPriceOutcomeLeg)) {
            return;
        }

        this.setCalendarizedState(matchingPriceOutcomeLeg.ticker);

        const legColor = getColor(matchingPriceOutcomeLeg);

        let sign = 1;

        if (isValidNumber(matchingPriceOutcomeLeg.qty, true)) {
            if (!isVoid(matchingPriceOutcomeLeg.side)) {
                sign = matchingPriceOutcomeLeg.side === MarketSide.Buy ? 1 : -1;
            }
        }

        this._legColor = legColor;
        this.qty = matchingPriceOutcomeLeg.qty * sign;
    }

    @DetectMethodChanges()
    private onRangeHighlightChanged() {

        if (this.isPmdColumn) {
            return;
        }

        if (this._params.component.isCustomRange) {
            this.highlightCustomTargetRange();
        } else {
            this.highlightAutoTargetRange();
        }
    }

    private highlightCustomTargetRange() {
        const component = this._params.component;
        const rowStrike = this._params.data.rowStrike;

        if (this._params.optionType === 'Call') {
            const callsStart = component.callsTargetRangeStart;
            const callsEnd = component.callsTargetRangeEnd;

            if (isValidNumber(callsStart) && isValidNumber(callsEnd)) {
                this.isInRange =
                    rowStrike >= Math.min(callsStart, callsEnd)
                    && rowStrike <= Math.max(callsStart, callsEnd);
            }
        } else {
            const putsStart = component.putsTargetRangeStart;
            const putsEnd = component.putsTargetRangeEnd;

            if (isValidNumber(putsStart) && isValidNumber(putsEnd)) {
                this.isInRange =
                    rowStrike >= Math.min(putsStart, putsEnd)
                    && rowStrike <= Math.max(putsStart, putsEnd);
            }
        }
    }

    private highlightAutoTargetRange() {
        const component = this._params.component;
        const rowStrike = this._params.data.rowStrike;


        const atmStrike = component.atmStrike || NaN;


        let colorPut = false;
        let colorCall = false;


        if (this._params.optionType === 'Call') {

            const callOffset = isValidNumber(component.callOffset)
                ? component.callOffset
                : NaN;

            const callRange = isValidNumber(component.callRange)
                ? component.callRange
                : NaN;

            const callsStart = atmStrike + callOffset;
            const callsEnd = callsStart + callRange;

            const boundariesAreValid = isValidNumber(callsStart)
                && isValidNumber(callsEnd);

            this.isInRange = boundariesAreValid
                && rowStrike >= Math.min(callsStart, callsEnd)
                && rowStrike <= Math.max(callsStart, callsEnd);
        }

        if (this._params.optionType === 'Put') {
            const putOffset = isValidNumber(component.putOffset)
                ? component.putOffset
                : NaN;

            const putRange = isValidNumber(component.putRange)
                ? component.putRange
                : NaN;

            const putsStart = atmStrike - putOffset;
            const putsEnd = putsStart - putRange;

            const boundariesAreValid = isValidNumber(putsStart)
                && isValidNumber(putsEnd);

            this.isInRange = boundariesAreValid
                && rowStrike >= Math.min(putsStart, putsEnd)
                && rowStrike <= Math.max(putsStart, putsEnd);
        }
    }

    ngOnDestroy() {
        if (!isVoid(this._currentTicker)) {
            // this._lastQuoteCache.unsubscribeTicker(this._currentTicker);
        }

        if (!isVoid(this._calendarizedTicker)) {
            this._lastQuoteCache.unsubscribeTicker(this._calendarizedTicker);
        }

        this._subscriptions.forEach(subscription => subscription.unsubscribe());
    }

    private highlightTransLegs(column: PricingGridStrategyColumn) {
        if (!this.isPmdColumn) {
            return;
        }

        if (this._params.pmdColumn !== 'trans') {
            return;
        }

        this._legColor = undefined;
        this.qty = undefined;
        this.calendarized = false;
        if (!isVoid(this._calendarizedTicker)) {
            this._lastQuoteCache.unsubscribeTicker(this._calendarizedTicker);
            this._calendarizedTicker = undefined;
        }
        this.dow = undefined;

        const legs = column.transLegs;


        const matchingPriceOutcomeLeg = legs
            .find(x => x.strike === this._params.data.rowStrike);

        if (isVoid(matchingPriceOutcomeLeg)) {
            return;
        }

        this.setCalendarizedState(matchingPriceOutcomeLeg.ticker);

        const legColor = getColor(matchingPriceOutcomeLeg);

        let sign = 1;

        if (isValidNumber(matchingPriceOutcomeLeg.qty, true)) {
            if (!isVoid(matchingPriceOutcomeLeg.side)) {
                sign = matchingPriceOutcomeLeg.side === MarketSide.Buy ? 1 : -1;
            }
        }

        this._legColor = legColor;
        this.qty = matchingPriceOutcomeLeg.qty * sign;
    }

    private highlightOwnLegs() {

        if (!this.isPmdColumn) {
            return;
        }

        if (this._params.pmdColumn !== 'own') {
            return;
        }

        this._legColor = undefined;
        this.qty = undefined;
        this.calendarized = false;
        if (!isVoid(this._calendarizedTicker)) {
            this._lastQuoteCache.unsubscribeTicker(this._calendarizedTicker);
            this._calendarizedTicker = undefined;
        }
        this.dow = undefined;

        const component = this._params.component;
        const type = this._params.optionType === 'Call' ? OptionType.Call : OptionType.Put;
        const targetSide = this._params.optionType === 'Call' ? 'calls' : 'puts';

        let hedges: HedgePosition[] = [];

        if (this._params.component.pmdHedgeMode === 'existing') {
            hedges = component.getExistingHedges(type).filter(x => !isVoid(x));
        } else if (component.pmdHedgeMode === 'custom') {
            hedges = component
                .getCustomLegs(targetSide).map(x => x.asHedgePosition())
                .filter(x => !isVoid(x));
        }

        const currentState = hedges
            .map(hp => {
                return {
                    ticker: hp.ticker,
                    qty: hp.qty
                }
            });

        const ownLegs = currentState.map(x => {
            const optionTicker = parseOptionTicker(x.ticker);
            return {
                ticker: x.ticker,
                qty: Math.abs(x.qty),
                side: x.qty > 0 ? MarketSide.Buy : MarketSide.Sell,
                strike: optionTicker?.strike
            } as PricingGridLeg
        });


        const matchingPriceOutcomeLeg = ownLegs
            .find(x => x.strike === this._params.data.rowStrike);

        if (isVoid(matchingPriceOutcomeLeg)) {
            return;
        }

        this.setCalendarizedState(matchingPriceOutcomeLeg.ticker);

        const legColor = getColor(matchingPriceOutcomeLeg);

        let sign = 1;

        if (isValidNumber(matchingPriceOutcomeLeg.qty, true)) {
            if (!isVoid(matchingPriceOutcomeLeg.side)) {
                sign = matchingPriceOutcomeLeg.side === MarketSide.Buy ? 1 : -1;
            }
        }

        this._legColor = legColor;
        this.qty = matchingPriceOutcomeLeg.qty * sign;

    }

    private setCalendarizedState(ticker: string) {
        const optionExpirationDate = this._params.component.selectedExpiration?.optionExpirationDate;
        if (isVoid(optionExpirationDate)) {
            return false;
        }

        this.calendarized = ticker.indexOf(optionExpirationDate) < 0;

        if (!this.calendarized) {
            return;
        }

        const optionTicker = parseOptionTicker(ticker);

        if (!isVoid(optionTicker)) {
            this.dow = makeDayOfWeekDate(optionTicker.expiration);
            this.price = undefined;
            if (!isVoid(this._calendarizedTicker)) {
                this._lastQuoteCache.unsubscribeTicker(this._calendarizedTicker);
            }
            this._calendarizedTicker = optionTicker.ticker;
            this._lastQuoteCache.subscribeTicker(this._calendarizedTicker);
        }
    }
}